import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "redux/hooks";

import { useTranslation } from "react-i18next";
import { useMediaQuery } from "context/media/MediaContext";

import { Col, ElementWrapper, Row } from "components/BaseComponents/Flex";
import { Button, Spinner, Typography } from "components";
import { DIGIPOSTHEME } from "theme/digiPosTheme";

import { TokenInclude } from "helpers/GetToken";

import {
  StyledBgWrapper,
  StyledTitleWrapper,
  StyledHr,
  StyledPdfDoc,
} from "./WorkingConditions.styles";
import Image, { ICONS } from "components/BaseComponents/Image";
import {
  DownloadPDF,
  WorkingConditionsAsync,
} from "services/employeeProfile.service";
import { setServiceError } from "redux/slices/ServiceInlineError";
import {
  PdfDocType,
  IWorkingConditionsResponse,
} from "models/Employee/employeeProfile.model";
import Alert from "components/BaseComponents/Alert/Alert";
import { setMenuClick } from "redux/slices/StepSlice";

const WorkingConditions: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  let fromIsFaq: boolean = location?.state?.isFaq || false;

  const token = TokenInclude();

  const [loading, setLoading] = useState<boolean>(false);
  const [workingData, setWorkingData] = useState<IWorkingConditionsResponse>();

  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [txt, setTxt] = useState<string>("");

  useEffect(() => {
    async function runWorkingConditions() {
      setLoading(true);
      const retVal = await WorkingConditionsAsync(token);
      if (retVal?.DialogBoxes?.length < 1) {
        setWorkingData(retVal);
        setLoading(false);
      } else {
        const errMessage: any = retVal;
        setLoading(false);
        dispatch(setServiceError(errMessage));
        return;
      }
    }
    runWorkingConditions();
  }, []);

  const pdfDownload = async (linkUrl: string, data: string, name: string) => {
    setLoading(true);
    const pdfByte = await DownloadPDF(linkUrl, data);
    if (!pdfByte) {
      setLoading(false);
      setIsAddError(true);
      setTxt(`${name} pdf indirilemedi!`);
      return;
    }

    try {
      const _url = window.URL || window.webkitURL;
      const pdflink = document.createElement("a");
      const downloadUrl = _url.createObjectURL(pdfByte);
      pdflink.href = downloadUrl;
      pdflink.download = `${name}.pdf`;
      pdflink.click();
      setTimeout(() => {
        setLoading(false);
        _url.revokeObjectURL(downloadUrl);
      }, 10000);
    } catch (error: any) {
      setLoading(false);
      setIsAddError(true);
      setTxt(`${name} pdf indirilemedi!`);
      return;
    }
  };

  const goBack = () => {
    if (fromIsFaq) {
      dispatch(setMenuClick(4));
      navigate("/faq-list");
    } else {
      navigate(-1);
    }
  };

  return (
    <ElementWrapper
      width='872px'
      height='calc(100vh + 369px)'
      direction='column'
      justifyContent='flex-start'
      alignItems='flex-start'
      margin='10px auto'
      padding={`${isMobile ? "0 5px !important" : ""}`}
    >
      <Spinner show={loading} />
      {isAddError && (
        <Alert
          alertType='Error'
          duration={9000}
          isVisible={isAddError}
          message={txt}
          setIsAddSuccess={setIsAddSuccess}
          setIsAddError={setIsAddError}
        />
      )}
      <StyledTitleWrapper>
        <Typography width='' size='16px' color={DIGIPOSTHEME.colors.white}>
          {t("Working_conditions")}
        </Typography>
      </StyledTitleWrapper>
      <StyledBgWrapper>
        <Row padding='10px 0 0 0'>
          <Typography
            display='flex'
            size={isMobile ? "14px" : "16px"}
            color={DIGIPOSTHEME.colors.darkGrey}
            weight='400'
            transform='uppercase'
          >
            {t("work_types")}
          </Typography>
          <Typography
            display='flex'
            margin='10px 0 0 0'
            size={isMobile ? "13px" : "15px"}
            color={DIGIPOSTHEME.colors.secondaryBlack}
            weight='600'
            transform='capitalize'
          >
            {workingData?.WorkType}
          </Typography>
        </Row>

        <StyledHr />

        <Row padding='15px 0 0 0'>
          <Typography
            display='flex'
            size={isMobile ? "14px" : "16px"}
            color={DIGIPOSTHEME.colors.darkGrey}
            weight='400'
            transform='uppercase'
          >
            {t("pos_fee")}
          </Typography>

          <Typography
            display='flex'
            margin='10px 0 0 0'
            size={isMobile ? "13px" : "15px"}
            color={DIGIPOSTHEME.colors.secondaryBlack}
            weight='600'
            transform='capitalize'
          >
            {workingData?.PosFee?.FullAmount}
          </Typography>
        </Row>
      </StyledBgWrapper>

      <Row
        width='100%'
        backgroundColor={DIGIPOSTHEME.colors.transparent}
        justifyContent='center'
      >
        <Typography
          width=''
          margin='10px 0'
          size={isMobile ? "15px" : "17px"}
          color={DIGIPOSTHEME.colors.secondaryGraySecondary}
        >
          {workingData?.InfoText}
        </Typography>
      </Row>

      <Row
        padding='25px 0'
        backgroundColor={DIGIPOSTHEME.colors.transparent}
        width='100%'
        noMargin
      >
        <Typography
          size={isMobile ? "14px" : "16px"}
          color={DIGIPOSTHEME.colors.darkGrey}
          weight='400'
          transform='uppercase'
        >
          {t("working_conditions_doc")}
        </Typography>

        {workingData?.DigitalDocumentList.map((pdf: PdfDocType, index) => (
          <StyledPdfDoc
            key={index}
            onClick={() =>
              pdfDownload(pdf.Link, pdf.ClientBelgeData, pdf.LinkText)
            }
          >
            <Typography
              margin='10px 0 0 0'
              size={isMobile ? "13px" : "15px"}
              color={DIGIPOSTHEME.colors.secondaryBlack}
              weight='600'
              transform='capitalize'
              hoverColor={DIGIPOSTHEME.colors.headerRed}
            >
              {pdf.LinkText}
            </Typography>
            <Image
              alt='PDF Icon'
              height='25px'
              srcSet={ICONS.PDF}
              width='25px'
            />
          </StyledPdfDoc>
        ))}
      </Row>

      <Row
        backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
        justifyContent='space-between'
        isResponsive
        noMargin
      >
        <Col maxWidth='130px'>
          <Button
            type='button'
            width={isMobile ? "100%" : "100px"}
            text={`← ${t("back")}`}
            onClick={() => goBack()}
            color='light'
          />
        </Col>
      </Row>
    </ElementWrapper>
  );
};

export default WorkingConditions;
